<script setup lang="ts">
  import { computed } from 'vue';
  import type { FormElement, CheckboxProperties } from '../../../model/form.types';
  import { useLabelPosition } from '../../../model/useLabelPosition';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
  import Checkbox from '@/shared/ui/checkboxes/Checkbox.vue';

  interface Props {
    element: FormElement & { properties: CheckboxProperties };
  }

  const props = defineProps<Props>();

  const { isLabelLeft, labelClasses, labelStyles } = useLabelPosition(computed(() => props.element.properties));

  const { containerStyles } = useElementContainerStyles(
    computed(() => props.element.properties),
    'CHECKBOX'
  );

  const options = computed(() => props.element.properties.options ?? []);

  const labelId = computed(() => `label-${props.element.id}`);

  const isHorizontal = computed(() => props.element.properties.orientation === 'horizontal');

  const optionLabelStyles = computed(() => {
    const p = props.element.properties;
    const styles: Record<string, string> = {
      fontSize: p.optionFontSize ? `${p.optionFontSize}px` : '10px',
      lineHeight: '1',
    };
    if (p.optionFontWeight === 'bold') styles.fontWeight = '700';
    else if (p.optionFontWeight === 'semibold') styles.fontWeight = '600';
    else if (p.optionFontWeight === 'normal') styles.fontWeight = '400';
    if (p.optionFontStyle === 'italic') styles.fontStyle = 'italic';
    if (p.optionTextDecoration && p.optionTextDecoration !== 'none') {
      styles.textDecoration = p.optionTextDecoration;
      styles.textDecorationSkipInk = 'none';
    }
    if (p.optionTextAlign) styles.textAlign = p.optionTextAlign;
    if (p.optionColor) styles.color = p.optionColor;
    return styles;
  });
</script>

<template>
  <div
    class="checkbox-element"
    :class="{ 'label-left': isLabelLeft }"
    :style="containerStyles"
  >
    <div
      v-if="isLabelLeft"
      class="label-container"
    >
      <label
        v-if="element.properties.label"
        :id="labelId"
        :class="labelClasses"
        :style="labelStyles"
      >
        {{ element.properties.label }}
      </label>
      <p
        v-if="element.properties.placeholder"
        class="text-[10px] text-gray-500 mt-1"
      >
        {{ element.properties.placeholder }}
      </p>
    </div>

    <template v-else>
      <label
        v-if="element.properties.label"
        :id="labelId"
        :class="labelClasses"
        class="block mb-2"
        :style="labelStyles"
      >
        {{ element.properties.label }}
        <span
          v-if="element.properties.placeholder"
          class="text-[10px] text-gray-500 font-normal ml-1"
        >
          ({{ element.properties.placeholder }})
        </span>
      </label>
    </template>

    <div
      class="checkbox-options"
      role="group"
      :aria-labelledby="element.properties.label ? labelId : undefined"
      aria-disabled="true"
      :class="{
        'ml-4': isLabelLeft,
        'checkbox-options--horizontal': isHorizontal,
        'checkbox-options--vertical': !isHorizontal,
      }"
    >
      <div
        v-for="option in options"
        :key="option.id"
        class="checkbox-option-item"
      >
        <label
          :for="`type-${option.id}`"
          class="flex items-center gap-1.5 cursor-pointer"
        >
          <Checkbox
            :id="option.id"
            :checked="false"
          />
          <span
            class="select-none text-gray-07 font-medium text-left"
            :style="optionLabelStyles"
          >
            {{ option.text }}
          </span>
        </label>
      </div>

      <div
        v-if="options.length === 0"
        class="text-gray-400 text-sm italic"
      >
        Nenhuma alternativa configurada
      </div>
    </div>
  </div>
</template>

<style scoped>
  .checkbox-element {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .checkbox-element.label-left {
    display: flex;
    align-items: flex-start;
    min-width: 0;
  }

  .checkbox-element.label-left .label-container {
    flex-shrink: 0;
    margin-right: 0.5rem;
  }

  .checkbox-element.label-left .label-container label {
    display: block;
    white-space: nowrap;
  }

  .checkbox-element.label-left .checkbox-options {
    flex: 1;
    min-width: 0;
  }

  .checkbox-options--vertical .checkbox-option-item {
    margin-bottom: 0.25rem;
  }

  .checkbox-options--vertical .checkbox-option-item:last-child {
    margin-bottom: 0;
  }

  .checkbox-options--horizontal {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .checkbox-options--horizontal .checkbox-option-item {
    margin-bottom: 0;
  }

  .checkbox-options {
    pointer-events: none;
    user-select: none;
  }

  .checkbox-option-item label {
    line-height: 1;
  }

  .checkbox-option-item :deep(input[type='checkbox']) {
    vertical-align: middle;
    background-color: white;
  }
</style>
